import { useQuery } from "@apollo/react-hooks";
import { createStyles, makeStyles, Theme } from "@material-ui/core";
import DateConstants from "Constants/DateConstants";
import {
  GET_FPSO_LEAGUE_TABLE_DATA,
  GetFpsoLeagueTableDataResult,
} from "Graphql/FpsoQueries";
import MaterialTable from "material-table";
import { LeagueTableRow } from "Models/LeagueTableRow";
import moment from "moment";
import React, { FC, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { TableIcons } from "Views/Components/Icons/TableIcons";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    leagueTable: {
      padding: theme.spacing(3),
    },
  }),
);

export const KPILeagueTable: FC = () => {
  const history = useHistory();
  const { t } = useTranslation("kpis");
  const classes = useStyles();

  const [tableData, setTableData] = useState<LeagueTableRow[]>([]);

  const {
    loading: dataLoading,
    error: dataError,
    data: leagueTableData,
  } = useQuery<GetFpsoLeagueTableDataResult>(GET_FPSO_LEAGUE_TABLE_DATA, {
    // onCompleted: handleLeagueTableDataReceived,
  });

  const handleLeagueTableDataReceived = useCallback(() => {
    const data: LeagueTableRow[] = [];
    leagueTableData?.fpsos.forEach((item) => {
      item.fpsoAreas.forEach((areaItem) => {
        const fpsoArea = leagueTableData?.fpsoAreas?.find(
          (f) => f.id === areaItem.id);
        if (!fpsoArea) {
          return;
        }
        data.push({
          fpsoAreaId: areaItem.id,
          fpsoName: areaItem.fpso.name,
          fpsoAreaName: areaItem.areaName,
          operatorName: areaItem.fpso.block.owner.companyName,
          grading:
            fpsoArea.currentAuditCarClassesCount.auditScore >= 40
              ? t("kpis:unacceptableGrading")
              : fpsoArea.currentAuditCarClassesCount.auditScore >= 30 &&
                fpsoArea.currentAuditCarClassesCount.auditScore <= 39
                ? t("kpis:unsatisfactoryGrading")
                : fpsoArea.currentAuditCarClassesCount.auditScore >= 20 &&
                fpsoArea.currentAuditCarClassesCount.auditScore <= 29
                  ? t("kpis:fairGrading")
                  : fpsoArea.currentAuditCarClassesCount.auditScore >= 0 &&
                  fpsoArea.currentAuditCarClassesCount.auditScore <= 19
                    ? t("kpis:goodGrading")
                    : "",
          currentAuditScore: fpsoArea.currentAuditCarClassesCount.auditScore,
          currentAuditStartDate: fpsoArea.currentAuditCarClassesCount.startDate!,
          countCriticals:
            fpsoArea.currentAuditCarClassesCount.countCriticals.aggregate.count,
          countMajors:
            fpsoArea.currentAuditCarClassesCount.countMajors.aggregate.count,
          countMinors:
            fpsoArea.currentAuditCarClassesCount.countMinors.aggregate.count,
          countComments:
            fpsoArea.currentAuditCarClassesCount.countComments.aggregate.count,
        });
      });
    });
    setTableData(data);

    // eslint-disable-next-line
  }, [leagueTableData]);

  useEffect(() => {
    handleLeagueTableDataReceived();

    // eslint-disable-next-line
  }, [leagueTableData]);

  const handleFpsoClick = (rowData: LeagueTableRow | LeagueTableRow[]) => {
    window.scrollTo(0, 0);
    if (Array.isArray(rowData)) {
      history.push(`/fpsoArea/${rowData[0].fpsoAreaId}`);
    } else {
      history.push(`/fpsoArea/${rowData.fpsoAreaId}`);
    }
  };

  return (
    <>
      {!dataError ? (
        <div className={classes.leagueTable}>
          <MaterialTable
            isLoading={dataLoading}
            options={{
              exportButton: false,
              exportAllData: false,
              draggable: false,
              padding: "default",
              pageSizeOptions: [10, 20, 30],
              pageSize: 10,
              emptyRowsWhenPaging: false,
              detailPanelColumnAlignment: "right",
              detailPanelType: "single",
            }}
            columns={[
              {
                title: t("fpsoName"),
                field: "fpsoName",
                headerStyle: {
                  fontWeight: "bold",
                },
              },
              {
                title: t("fpsoAreaName"),
                field: "fpsoAreaName",
                headerStyle: {
                  fontWeight: "bold",
                },
              },
              {
                title: t("operator"),
                field: "operatorName",
                headerStyle: {
                  fontWeight: "bold",
                },
              },
              {
                title: t("currentGrading"),
                field: "grading",
                cellStyle: {
                  textAlign: "center",
                },
                headerStyle: {
                  textAlign: "center",
                  fontWeight: "bold",
                },
              },
              {
                title: t("currentAuditScore"),
                field: "currentAuditScore",
                sorting: true,
                cellStyle: {
                  textAlign: "center",
                },
                headerStyle: {
                  textAlign: "center",
                  fontWeight: "bold",
                },
              },
              {
                title: t("currentAuditDate"),
                field: "currentAuditStartDate",
                type: "date",
                render: (rowData) => (
                  <>
                    {rowData.currentAuditStartDate ? (
                      <>
                        {moment(rowData.currentAuditStartDate).format(
                          DateConstants.DEFAULT_DATE_FORMAT,
                        )}
                      </>
                    ) : (
                      <>{t("cars:notAvailable")}</>
                    )}
                  </>
                ),
                cellStyle: {
                  textAlign: "center",
                },
                headerStyle: {
                  textAlign: "center",
                  fontWeight: "bold",
                },
              },
              {
                title: t("openCriticalsTitle"),
                field: "countCriticals",
                cellStyle: {
                  textAlign: "center",
                },
                headerStyle: {
                  textAlign: "center",
                  fontWeight: "bold",
                },
              },
              {
                title: t("openMajorsTitle"),
                field: "countMajors",
                cellStyle: {
                  textAlign: "center",
                },
                headerStyle: {
                  textAlign: "center",
                  fontWeight: "bold",
                },
              },
              {
                title: t("openMinorsTitle"),
                field: "countMinors",
                cellStyle: {
                  textAlign: "center",
                },
                headerStyle: {
                  textAlign: "center",
                  fontWeight: "bold",
                },
              },
              {
                title: t("openCommentsTitle"),
                field: "countComments",
                cellStyle: {
                  textAlign: "center",
                },
                headerStyle: {
                  textAlign: "center",
                  fontWeight: "bold",
                },
              },
            ]}
            icons={TableIcons}
            data={tableData}
            title={t("leagueTableTitle")}
            onRowClick={(event, rowData) => {
              if (rowData) {
                handleFpsoClick(rowData);
              }
            }}
          />
        </div>
      ) : (
        <div>{t("errors:leagueTableFetchError")}</div>
      )}
    </>
  );
};
